import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  BookOpen01,
  Building02,
  CalendarDate,
  Check,
  ChevronDown,
  ChevronUp,
  FileCheck02,
  Globe01,
  PieChart01,
  Settings01,
  Spinner,
  Ticket01,
  Tool02,
  UserSquare,
  Users01,
  XClose
} from '@opendate-app/setlist';
import { conductorTheme } from '../themeTokens';

const TOOL_ICONS = {
  'venue_details': Building02,
  'contact_lookup': UserSquare,
  'venue_events': CalendarDate,
  'venue_ticket_sales': Ticket01,
  'venue_financials': PieChart01,
  'venue_offers': FileCheck02,
  'venue_fan_analytics': Users01,
  'web_search': Globe01,
  'help_docs': BookOpen01
};

const ToolCallIndicator = ({ toolCall, isActive }) => {
  const Icon = TOOL_ICONS[toolCall.tool_name] || Settings01;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      backgroundColor: isActive ? conductorTheme.accentSurface : conductorTheme.surfaceSubtle,
      border: `1px solid ${isActive ? 'var(--od-accent-border, #bae6fd)' : conductorTheme.border}`,
      borderRadius: '8px',
      marginBottom: '8px',
      fontSize: '12px',
      transition: 'background-color 200ms ease, border-color 200ms ease'
    }}>
      <div style={{
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        background: isActive
          ? 'linear-gradient(135deg, #ff6b35 0%, #ff8c00 100%)'
          : conductorTheme.border,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {isActive ? (
          <span style={{ display: 'inline-flex', color: '#fff' }}>
            <Spinner size={11} color="currentColor" />
          </span>
        ) : (
          <Icon width={11} height={11} aria-hidden="true" style={{ color: conductorTheme.textFaint }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 500,
          color: conductorTheme.textBody,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {toolCall.display_name}
        </div>
        {toolCall.summary && !isActive && (
          <div style={{
            fontSize: '11px',
            color: toolCall.success ? '#059669' : '#dc2626',
            marginTop: '2px'
          }}>
            {toolCall.success ? (
              <><Check width={11} height={11} aria-hidden="true" style={{ marginRight: '4px', verticalAlign: '-1px' }} />{toolCall.summary}</>
            ) : (
              <><XClose width={11} height={11} aria-hidden="true" style={{ marginRight: '4px', verticalAlign: '-1px' }} />{toolCall.summary}</>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ToolCallIndicator.propTypes = {
  toolCall: PropTypes.shape({
    tool_name: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    success: PropTypes.bool,
    summary: PropTypes.string
  }).isRequired,
  isActive: PropTypes.bool
};

ToolCallIndicator.defaultProps = {
  isActive: false
};

export const CollapsibleToolCalls = ({ activeToolCalls, completedToolCalls }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalCompleted = completedToolCalls.length;
  const hasActive = activeToolCalls.length > 0;

  if (totalCompleted === 0 && !hasActive) return null;

  return (
    <div>
      {activeToolCalls.map((toolCall, index) => (
        <ToolCallIndicator key={`active-${index}`} toolCall={toolCall} isActive={true} />
      ))}

      {totalCompleted > 0 && (
        <>
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              backgroundColor: conductorTheme.surfaceSubtle,
              border: `1px solid ${conductorTheme.border}`,
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = conductorTheme.surfaceMuted}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = conductorTheme.surfaceSubtle}
          >
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              backgroundColor: conductorTheme.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Tool02 width={11} height={11} aria-hidden="true" style={{ color: conductorTheme.textFaint }} />
            </div>
            <div style={{ flex: 1, minWidth: 0, fontWeight: 500, color: conductorTheme.textBody }}>
              {totalCompleted} tool call{totalCompleted !== 1 ? 's' : ''}
            </div>
            {isExpanded ? (
              <ChevronUp width={10} height={10} aria-hidden="true" style={{ color: conductorTheme.textSubtle }} />
            ) : (
              <ChevronDown width={10} height={10} aria-hidden="true" style={{ color: conductorTheme.textSubtle }} />
            )}
          </div>

          {isExpanded && (
            <div style={{ marginLeft: '16px', borderLeft: `2px solid ${conductorTheme.border}`, paddingLeft: '12px' }}>
              {completedToolCalls.map((toolCall, index) => (
                <ToolCallIndicator key={`completed-${index}`} toolCall={toolCall} isActive={false} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

CollapsibleToolCalls.propTypes = {
  activeToolCalls: PropTypes.array,
  completedToolCalls: PropTypes.array
};

CollapsibleToolCalls.defaultProps = {
  activeToolCalls: [],
  completedToolCalls: []
};

export default ToolCallIndicator;
